import { useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";
import { isElectronRuntime } from "@/lib/logout";
import { API_URL } from "@/lib/config";
import { checkMinVersion } from "@/lib/min-version-check";
import { resolveCurrentVersion } from "@/lib/current-version";
import type { UpdaterState } from "@/hooks/use-electron-updater";
import type { UpdateRequiredReason } from "@/components/UpdateRequiredDialog";

interface UseElectronMinVersionGateArgs {
  userId: string | undefined;
  electronUpdaterState: UpdaterState | null;
  setRequiresUpdate: Dispatch<SetStateAction<UpdateRequiredReason | null>>;
}

/**
 * Proactive min-version check: runs once per auth session, Electron-only.
 * Fails open on network error.
 *
 * Web-guarded because `__APP_VERSION__` is baked into the CDN-served bundle
 * at build time, and the web "reload" CTA just re-fetches the same bundle —
 * so a web session with a stale bundle would be permanently blocked until
 * the web deploy caught up. `MIN_DESKTOP_VERSION` is semantically a gate
 * for the packaged Electron app only.
 *
 * Dep array uses the extracted string (not the full state object) because
 * the updater's `downloading` state mutates object identity on every
 * progress tick — depending on the object would fire a burst of
 * `/api/min-version` requests during any update download.
 *
 * Extracted verbatim from AuthGate; the dependency array
 * `[userId, currentVersionForCheck]` is preserved exactly.
 */
export function useElectronMinVersionGate({
  userId,
  electronUpdaterState,
  setRequiresUpdate,
}: UseElectronMinVersionGateArgs): void {
  const currentVersionForCheck = electronUpdaterState?.currentVersion ?? null;
  useEffect(() => {
    if (!userId || !isElectronRuntime()) return;
    let cancelled = false;
    const currentVersion = resolveCurrentVersion(electronUpdaterState);
    void checkMinVersion({ apiBaseUrl: API_URL, currentVersion }).then(
      (violation) => {
        if (cancelled || !violation) return;
        setRequiresUpdate((prev) =>
          prev
            ? prev
            : {
                kind: "min-version",
                currentVersion: violation.currentVersion,
                minVersion: violation.minVersion,
                message: violation.message,
              },
        );
      },
    );
    return () => {
      cancelled = true;
    };
    // electronUpdaterState is read for resolveCurrentVersion, but we depend
    // only on the stable string to avoid progress-tick churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentVersionForCheck]);
}
